import { openModal } from '@mantine/modals';
import type { Id } from 'api/models';
import classNames from 'classnames/bind';
import { Panel } from 'components/index';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { isNonNullish } from 'remeda';
import ActivityDetail from 'shared/components/ActivityDetail';
import { getTitleKeyForActivity } from 'shared/components/ActivityDetail/ActivityDetail';
import Conversation from 'shared/components/ActivityDetail/Conversation';
import { EmailDeliveryStatusCompact } from 'shared/components/ActivityDetail/EmailDeliveryStatusCompact';
import Amount from 'shared/components/Amount';
import HTML from 'shared/components/HTML/HTML';
import { Icon, IconName } from 'shared/components/Icon';
import { Button, ButtonColor } from 'shared/io';
import { treatActivity } from 'shared/serializer';
import { accountActions } from 'store/account/account.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { OnClickFunction } from 'types/html-type';
import type { Activity } from 'types/store/activity';
import type { NextStepInformation } from 'types/store/invoice-state';
import type { MetaData } from 'types/storeTypes';
import Invoice from '..';
import styleIdentifiers from './InvoiceTimeline.module.scss';

const styles = classNames.bind(styleIdentifiers);

const activityType = {
  payment: 'wallet',
  comment: 'comment',
  paused: 'paused',
  lawyer: 'law',
  bailiff: 'balance',
  formal_notice: 'formal-notice',
  preference: 'sending-options',
  reminders: 'send',
  import: 'import-file',
  warning: 'alert-circle-exc',
  task: 'info',
  time: 'later',
  valid: 'check',
  update: 'customization',
};

type Props = {
  activities: Activity[];
  nextAction?: any | NextStepInformation;
  metadata?: MetaData;
  seeMore?: OnClickFunction;
  invoiceId?: number;
};

export default function InvoiceTimeline({
  activities,
  nextAction,
  metadata,
  seeMore,
  invoiceId,
}: Props) {
  const { t } = useTranslation();

  const [selectedActivityId, setSelectedActivityId] = useState<Id | null>();

  const downloadDocument = (_activity: Activity) => () => {
    invoiceActions.document({
      id: _activity.tracked_id,
      type: _activity.document_type,
      ...(isNonNullish(_activity.document_id) ? { document_id: _activity.document_id } : {}),
    });
  };

  const showCreditNote = (_activity: Activity) => () => {
    showDialog({
      id: DialogShowId.INVOICE,
      size: DialogShowSize.LARGE,
      children: <Invoice isCreditNote id={_activity.linked_credit_note_id} modal />,
    });
  };

  const showConversation = (activity: Activity) => () => {
    const title = t(
      (activity.activity_type === 'lawyer' && i18nKeys.INVOICE.LAWYER_CASE) ||
        (activity.tracked_type === 'Invoice' && i18nKeys.ACTIVITIES.UPDATE_STATUS_INVOICE) ||
        i18nKeys.NO_TEXT,
    );

    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title,
      children: (
        <Conversation
          className={styles('conversation')}
          invoiceFeedback={activity.invoice_feedback}
          invoiceId={invoiceId}
          caseId={activity.invoice_feedback?.third_party_case_id ?? null}
          onSubmit={() => {}}
        />
      ),
    });
  };

  const getGapDate = (index: number) => {
    if (index + 1 !== activities.length) {
      const currentActivity = dayjs(activities[index].date).startOf('day');
      const nextActivity = activities[index + 1].date;
      if (!currentActivity.isSame(nextActivity, 'day')) {
        const diffDays = Math.ceil(currentActivity.diff(nextActivity, 'day'));
        return `+ ${t(i18nKeys.DAYS, {
          count: diffDays,
        })}`;
      }
    }
    return '';
  };

  const queryString = qs.parse(location.search);
  const activityId = queryString.activityId ?? selectedActivityId;

  useEffect(() => {
    if (activityId) {
      accountActions.activityDetail({
        id: Number(activityId),
        callback: (res: any) => {
          openModal({
            size: 'auto',
            title: t(getTitleKeyForActivity(treatActivity(res.data))),
            children: <ActivityDetail activity={treatActivity(res.data)} />,
            onClose: () => {
              setSelectedActivityId(null);
              const url = new URL(window.location.href);
              url.searchParams.delete('activityId');
              window.history.pushState({}, '', url.toString());
            },
          });
        },
      });
    }
  }, [activityId]);

  if (activities == null) return null;

  return (
    <Panel
      withBorder
      title={`${t(i18nKeys.INVOICE.ACTIVITIES)}`}
      className={styles('invoice-timeline')}
    >
      {nextAction && (
        <div className={styles('activity', 'next-action')}>
          <div className={styles('info')}>
            <div className={styles('author')}>{t(i18nKeys.INVOICE.NEXT_STEP)}</div>
            <div>{dayjs().format('L')}</div>
          </div>
          <div className={styles('timeline')}>
            <div className={styles('circle')} />
            <div className={styles('line')} />
          </div>

          <div className={styles('description')}>
            <HTML html={nextAction} />
          </div>
        </div>
      )}
      {activities.map((activity, index) => (
        <div key={activity.id} className={styles('activity')}>
          <div className={styles('info')}>
            <div className={styles('author')}>{activity.author}</div>
            <div>{dayjs(activity.date).format('L, LT')}</div>
          </div>
          <div className={styles('timeline')}>
            <div className={styles('circle')}>
              <Icon
                name={
                  (activityType[activity.activity_type as keyof typeof activityType] ||
                    'invoice') as IconName
                }
              />
            </div>
            <div className={styles('line')}>
              <span className={styles('gap-date')}>{getGapDate(index)}</span>
            </div>
          </div>

          <div
            className={styles('description', setSelectedActivityId != null && 'clickable')}
            onClick={() => setSelectedActivityId?.(activity.id)}
          >
            <HTML html={activity.description} />
            {activity.comment && (
              <span>
                <span className={styles('subtitle')}>{t(i18nKeys.COMMENT)}</span> :{' '}
                <span className={styles('comment')}>{activity.comment}</span>
              </span>
            )}
            {activity.reason && (
              <span>
                <span className={styles('subtitle')}>{t(i18nKeys.REASON)}</span> :{' '}
                <span className={styles('comment')}>{activity.reason}</span>
              </span>
            )}
            {activity.end_pause_at && (
              <div>
                <span className={styles('subtitle')}>{t(i18nKeys.END_OF_PAUSE)}</span> :{' '}
                {dayjs(activity.end_pause_at).format('L')}
              </div>
            )}
            {activity.payment_information && (
              <div className={styles('payment-info')}>
                <div>
                  <b>{t(i18nKeys.AMOUNT)} : </b>
                  <Amount
                    localizedValue={activity.payment_information.amount}
                    value={activity.payment_information.localized_money_object.amount}
                    suffix={activity.payment_information.currency}
                  />
                </div>
                <div>
                  <b>{t(i18nKeys.DATE)} : </b>
                  {dayjs(activity.payment_information.date).format('L')}
                </div>
                <div>
                  <b>{t(i18nKeys.MODALITY)} : </b>
                  <span>{activity.payment_information.modality}</span>
                </div>
                {activity.payment_information.notes && (
                  <div>
                    <b>{t(i18nKeys.NOTES)} : </b>
                    <span>{activity.payment_information.notes}</span>
                  </div>
                )}
                {activity.payment_information.counterparty_name && (
                  <div>
                    <b>{t(i18nKeys.ACTIVITIES.COUNTERPART)} : </b>
                    <span>{activity.payment_information.counterparty_name}</span>
                  </div>
                )}
                {activity.payment_information.external_id && (
                  <div>
                    <b>{t(i18nKeys.ACTIVITIES.EXTERNAL_ID)} : </b>
                    <span>{activity.payment_information.external_id}</span>
                  </div>
                )}
              </div>
            )}
            <div className={styles('more')}>
              {activity.linked_credit_note_id && (
                <Button
                  className={styles('show-conversation')}
                  iconRight={IconName.EYE}
                  onClick={e => {
                    e.stopPropagation();
                    showCreditNote(activity)();
                  }}
                  label={t(i18nKeys.ACTIVITIES.SEE_CREDIT_NOTE)}
                  small
                  noMargin
                  color={ButtonColor.WHITE}
                />
              )}
              {activity.invoice_feedback && (
                <Button
                  className={styles('show-conversation')}
                  iconRight={IconName.EYE}
                  onClick={e => {
                    e.stopPropagation();
                    showConversation(activity)();
                  }}
                  label={t(i18nKeys.SHOW_CONVERSATION)}
                  small
                  noMargin
                  color={ButtonColor.WHITE}
                />
              )}
              {activity.email_statuses && (
                <EmailDeliveryStatusCompact statuses={activity.email_statuses} />
              )}
              {activity.has_document && (
                <Icon
                  name={IconName.PDF}
                  onClick={e => {
                    e.stopPropagation();
                    downloadDocument(activity)();
                  }}
                  className={styles('download-document')}
                  title={t(i18nKeys.ACTIVITIES.DOWNLOAD_DOCUMENT)}
                />
              )}
            </div>
          </div>
        </div>
      ))}

      {metadata?.pagination.current_page !== metadata?.pagination.last_page && (
        <div className={styles('see-more')} onClick={seeMore}>
          {t(i18nKeys.SEE_MORE)} <Icon name={IconName.ARROW_BOTTOM_ROUNDED} />
        </div>
      )}
    </Panel>
  );
}
